import { defaultShouldDehydrateQuery, type InfiniteData } from "@tanstack/react-query";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { PersistedClient } from "@tanstack/react-query-persist-client";
import { env } from "@/config/env";
import type { SearchPosting, SearchResults } from "@/lib/api/catalogue";
import { cutAt } from "./letters";

/** Searches stay fresh for five minutes and available while refreshing for thirty. */
export const SEARCH_FRESH_MS = 5 * 60_000;
export const CATALOGUE_CACHE_MS = 30 * 60_000;
const MAX_QUERIES = 30;
const MAX_BYTES = 2_000_000;
const PUBLIC_READS = new Set(["search", "page", "counts", "status", "places", "titles", "here"]);
/** A list shows a posting's opening. The drawer's box holds 800 letters at most, so more always fades. */
const DESCRIPTION_KEPT = 1000;
/** Saving blocks the page, so it waits this long after the last save. */
const SAVE_EVERY_MS = 3_000;

type SavedQuery = PersistedClient["clientState"]["queries"][number];

/** Only public catalogue reads belong on disk. Never persist a person's data or mutations. */
export const catalogueDehydrateOptions = {
    shouldDehydrateMutation: () => false,
    shouldDehydrateQuery: (query: Parameters<typeof defaultShouldDehydrateQuery>[0]) =>
        defaultShouldDehydrateQuery(query) &&
        query.queryKey[0] === "catalogue" &&
        PUBLIC_READS.has(String(query.queryKey[1])) &&
        Date.now() - query.state.dataUpdatedAt < CATALOGUE_CACHE_MS,
};

/** A search on disk keeps what a list shows: its opening in its shape, not the whole text or the keywords. */
function slimPosting(posting: Partial<SearchPosting>): Partial<SearchPosting> {
    const slim = { ...posting };
    if (typeof slim.description === "string") {
        slim.description = cutAt(slim.description, DESCRIPTION_KEPT);
    }
    if (Array.isArray(slim.keywords)) slim.keywords = [];
    return slim;
}

/** A page stays the same object from save to save, so each is slimmed once. */
const slimmed = new WeakMap<object, unknown>();

/** A save never fails on a shape it does not expect: that is kept as it came. */
function slimResults(results: unknown): unknown {
    const page = results as Partial<SearchResults> | null;
    if (!page || !Array.isArray(page.data)) return results;
    let slim = slimmed.get(page);
    if (slim === undefined) {
        slim = { ...page, data: page.data.map(slimPosting) };
        slimmed.set(page, slim);
    }
    return slim;
}

function slimQuery(query: SavedQuery): SavedQuery {
    const read = query.queryKey[1];
    const before = query.state.data;
    let data = before;
    if (read === "page") data = slimResults(before);
    if (read === "search") {
        const search = before as Partial<InfiniteData<SearchResults>> | null;
        if (Array.isArray(search?.pages))
            data = { ...search, pages: search.pages.map(slimResults) };
    }
    return data === before ? query : { ...query, state: { ...query.state, data } };
}

/**
 * Keep recent searches within a small storage budget, even after a long
 * browsing session. Each search is measured once, the newest first.
 */
function serialize(client: PersistedClient): string {
    const recent = [...client.clientState.queries]
        .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
        .slice(0, MAX_QUERIES)
        .map(slimQuery);
    const empty = { ...client, clientState: { mutations: [], queries: [] as SavedQuery[] } };
    let length = JSON.stringify(empty).length;
    const queries: SavedQuery[] = [];
    for (const query of recent) {
        // One more search adds itself and a comma.
        length += JSON.stringify(query).length + 1;
        if (length * 2 > MAX_BYTES) break;
        queries.push(query);
    }
    return JSON.stringify({ ...empty, clientState: { mutations: [], queries } });
}

/** A tab's catalogue cache survives reloads. Blocked or full storage must never prevent a search. */
export function cataloguePersister() {
    let storage: Storage | undefined;
    try {
        storage = typeof window === "undefined" ? undefined : window.sessionStorage;
    } catch {
        // Storage may be disabled.
    }
    return createAsyncStoragePersister({
        storage: storage
            ? {
                  getItem: (key) => {
                      try {
                          return storage.getItem(key);
                      } catch {
                          return null;
                      }
                  },
                  setItem: (key, value) => storage.setItem(key, value),
                  removeItem: (key) => {
                      try {
                          storage.removeItem(key);
                      } catch {
                          // A blocked cache is already unavailable.
                      }
                  },
              }
            : undefined,
        key: `jobenvoy:catalogue:v1:${env.API_URL}:${env.SAMPLE_DATA}`,
        throttleTime: SAVE_EVERY_MS,
        serialize,
    });
}
